import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { OrigemSolicitacaoCodigo } from '@shared/enums/assinatura-eletronica.enum';
import { WebSocketMessageBody } from '@shared/interfaces/websocket';
import SockJS from 'sockjs-client';
import { Client, Message, over } from 'webstomp-client';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class WebSocketAssinaturaService {
  #toastService = inject(ToastService);
  #stompClient: Client | null = null;
  #isConnected = false;
  #subscriptions = new Map<string, { unsubscribe: () => void }>();

  /** Inicializa o WebSocket */
  private initClient() {
    const wsUrl = environment.backendUrl + 'websocket';
    const ws = new SockJS(wsUrl);
    this.#stompClient = over(ws, {
      debug: false,
    });
  }

  /** Conecta ao WebSocket */
  conectarComWebSocket(): void {
    if (!this.#stompClient) {
      this.initClient();
    }

    this.#stompClient?.connect(
      this.obterHeadersAutenticacao(),
      () => {
        this.#isConnected = true;
      },
      (error) => {
        console.error('Erro ao conectar ao WebSocket:', error);
        this.#isConnected = false;
      }
    );
  }

  /** Subscreve a um endpoint específico */
  subscribeToEndpoint(topic: string, callback: (message: WebSocketMessageBody) => void): void {
    if (!this.#stompClient || !this.#isConnected) {
      console.warn('Tentativa de subscrição antes da conexão.');
      return;
    }

    this.unsubscribeFromEndpoint(topic);

    const subscription = this.#stompClient.subscribe(topic, (message: Message) => {
      const parsedMessage = JSON.parse(message.body);
      callback(parsedMessage);
    });

    this.#subscriptions.set(topic, subscription);
  }

  unsubscribeFromEndpoint(topic: string): void {
    const subscription = this.#subscriptions.get(topic);
    subscription?.unsubscribe();
    this.#subscriptions.delete(topic);
  }

  /** Envia uma mensagem pelo WebSocket */
  sendMessage(destination: string, message: unknown): void {
    if (!this.#stompClient || !this.#isConnected) {
      console.warn('Tentativa de envio antes da conexão.');
      return;
    }

    this.#stompClient.send(destination, JSON.stringify(message), {});
  }

  /** Desconecta do WebSocket */
  desconectarWebSocket(): void {
    this.#subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.#subscriptions.clear();

    if (this.#stompClient) {
      this.#stompClient.disconnect(() => {
        this.#isConnected = false;
      });
    }
  }

  /** Obtém o token de autenticação */
  private obterHeadersAutenticacao(): { Authorization: string } {
    const token = localStorage.getItem('gesthosp_user_pep') || '';
    return { Authorization: `Bearer ${token}` };
  }

  public validarAssinatura(
    message: WebSocketMessageBody,
    codigo: OrigemSolicitacaoCodigo
  ): boolean {
    if (message.codigo !== codigo) {
      return false;
    }

    if (message.expirado === true) {
      this.#toastService.toastErro(
        'Falha ao assinar',
        'A assinatura não foi concluída dentro do tempo limite.'
      );
      return false;
    }

    if (message.sucesso !== true) {
      this.#toastService.toastErro(
        'Falha ao assinar',
        'Ocorreu um erro na assinatura eletrônica, tente novamente.'
      );
      return false;
    }

    return true;
  }
}
